import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSEO } from "../lib/useSEO";
import { Check, X, Star } from "lucide-react";
import AdminNav from "../components/AdminNav";

export default function AdminModeration() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useSEO({ title: "Moderation | Admin" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews", { params: { status: "pending" } });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (!authLoading && !user) return <Navigate to="/admin/login" replace />;

  const approve = async (id) => {
    setBusy((b) => ({ ...b, [id]: "ok" }));
    try { await api.post(`/admin/reviews/${id}/approve`); await load(); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };
  const reject = async (id) => {
    if (!confirm("Reject and delete this review?")) return;
    setBusy((b) => ({ ...b, [id]: "del" }));
    try { await api.delete(`/admin/reviews/${id}`); await load(); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  return (
    <main className="min-h-screen bg-stone-50">
      <AdminNav />

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-extrabold text-stone-900 mb-6">Review moderation</h1>

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-500">
            All caught up — no pending reviews.
          </div>
        ) : (
          <div className="space-y-6" data-testid="moderation-list">
            {items.map((r) => (
              <article key={r.id} className="rounded-2xl border border-stone-200 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {r.user_picture ? <img src={r.user_picture} className="w-9 h-9 rounded-full" alt="" referrerPolicy="no-referrer"/> : <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center font-semibold">{(r.user_name||"?")[0]}</div>}
                    <div>
                      <p className="font-semibold text-stone-900 text-sm">{r.user_name}</p>
                      <p className="text-xs text-stone-500">on <Link to={`/waterfalls/${r.waterfall_slug}`} className="underline">{r.waterfall_name || r.waterfall_slug}</Link></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-stone-900 text-stone-900" : "text-stone-300"}`} />)}
                  </div>
                </div>
                <p className="text-stone-800 leading-relaxed whitespace-pre-line mb-3">{r.text}</p>
                {r.image_urls?.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    {r.image_urls.map((u,i) => <img key={i} src={u + "?w=200"} alt="" className="w-24 h-24 object-cover rounded-lg" />)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={() => approve(r.id)} disabled={busy[r.id]} data-testid={`approve-${r.id}`} className="btn-at btn-at-green text-sm disabled:opacity-50">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </button>
                  <button onClick={() => reject(r.id)} disabled={busy[r.id]} data-testid={`reject-${r.id}`} className="btn-at border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-50">
                    <X className="w-4 h-4 mr-1" /> Reject
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
